import {
  CAB_AGENDA_DEFAULT_ORDER,
  CAB_AGENDA_DEFAULT_STATUS,
  CAB_AGENDA_SHORT_DESCRIPTION_MAX,
  CAB_AGENDA_STATUS_OPTIONS,
} from '../constants/cabAgendaOptions';
import type { CabAgendaItem, CabAgendaItemFormData, CabAgendaItemStatus } from '../types/cabAgendaItem';

export type CabAgendaItemFormErrors = Partial<Record<keyof CabAgendaItemFormData, string>>;

export function createDefaultAgendaItemFormData(
  meetingName: string,
  nextSortOrder: number = CAB_AGENDA_DEFAULT_ORDER
): CabAgendaItemFormData {
  return {
    meetingName,
    changeRequestId: '',
    changeNumber: '',
    shortDescription: '',
    status: CAB_AGENDA_DEFAULT_STATUS,
    sortOrder: nextSortOrder,
  };
}

function toAgendaStatus(status: string): CabAgendaItemStatus {
  const match = CAB_AGENDA_STATUS_OPTIONS.find((o) => o.value === status);
  return match?.value ?? CAB_AGENDA_DEFAULT_STATUS;
}

export function agendaItemToFormData(item: CabAgendaItem): CabAgendaItemFormData {
  return {
    meetingName: item.meetingName,
    changeRequestId: item.changeRequestId,
    changeNumber: item.changeNumber,
    shortDescription: item.shortDescription,
    status: toAgendaStatus(item.status),
    sortOrder: item.sortOrder,
  };
}

export function validateCabAgendaItemForm(data: CabAgendaItemFormData): CabAgendaItemFormErrors {
  const errors: CabAgendaItemFormErrors = {};

  if (!data.meetingName.trim()) {
    errors.meetingName = 'Meeting is required';
  }

  if (!data.changeRequestId || !data.changeNumber.trim()) {
    errors.changeNumber = data.changeNumber.trim()
      ? 'Please select a Change Request from the list or lookup'
      : 'Task is required';
  }

  if (!data.status) {
    errors.status = 'Status is required';
  }

  const order = Number(data.sortOrder);
  if (data.sortOrder === '' || Number.isNaN(order)) {
    errors.sortOrder = 'Order is required';
  } else if (!Number.isInteger(order) || order < 1) {
    errors.sortOrder = 'Order must be a positive integer';
  }

  const shortDescription = data.shortDescription.trim();
  if (!shortDescription) {
    errors.shortDescription = 'Short Description is required';
  } else if (shortDescription.length > CAB_AGENDA_SHORT_DESCRIPTION_MAX) {
    errors.shortDescription = `Short Description must be ${CAB_AGENDA_SHORT_DESCRIPTION_MAX} characters or fewer`;
  }

  return errors;
}

export function hasAgendaValidationErrors(errors: CabAgendaItemFormErrors): boolean {
  return Object.keys(errors).length > 0;
}
